import { useAuth0 } from '@auth0/auth0-react';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { intakeQuestionnaireActions } from '../../../../../../../store';
import { hasFamilyFieldValue } from '../../../../../../common';

import type { AppDispatch, RootState } from '../../../../../../../store';
import type { FamilyAdditionalInfoType } from '../../../../../../common';

export default function useIntakeFamilyAdditionalInfo() {
  const dispatch = useDispatch<AppDispatch>();
  const { getAccessTokenSilently } = useAuth0();
  const { intakeQuestionnaireId } = useParams<'intakeQuestionnaireId'>();

  const family = useSelector(
    (state: RootState) => state.intakeQuestionnaire.intakeQuestionnaireDetails?.family,
  );
  // Covers the whole Family Information section: additional-info items and the cultural
  // consideration field (which patches the family via updateIntakeFamily).
  const isSaving = useSelector(
    (state: RootState) =>
      state.intakeQuestionnaire.createFamilyAdditionalInfoLoading ||
      state.intakeQuestionnaire.deleteFamilyAdditionalInfoLoading ||
      state.intakeQuestionnaire.updateFamilyLoading,
  );

  const create = useCallback(
    async (infoType: FamilyAdditionalInfoType, value: string) => {
      if (!intakeQuestionnaireId) return false;
      const result = await dispatch(
        intakeQuestionnaireActions.createIntakeFamilyAdditionalInfo({
          intakeQuestionnaireId,
          data: { info_type: infoType, value },
          getAccessTokenSilently,
        }),
      );
      return intakeQuestionnaireActions.createIntakeFamilyAdditionalInfo.fulfilled.match(result);
    },
    [dispatch, getAccessTokenSilently, intakeQuestionnaireId],
  );

  const remove = useCallback(
    async (infoType: FamilyAdditionalInfoType, itemId: number) => {
      if (!intakeQuestionnaireId) return false;
      const result = await dispatch(
        intakeQuestionnaireActions.deleteIntakeFamilyAdditionalInfo({
          intakeQuestionnaireId,
          infoType,
          itemId,
          getAccessTokenSilently,
        }),
      );
      return intakeQuestionnaireActions.deleteIntakeFamilyAdditionalInfo.fulfilled.match(result);
    },
    [dispatch, getAccessTokenSilently, intakeQuestionnaireId],
  );

  return {
    additionalInfo: {
      maternal_medical_history: family?.maternal_medical_history ?? [],
      paternal_medical_history: family?.paternal_medical_history ?? [],
      community_support: family?.community_support ?? [],
    },
    hasSavedValues: hasFamilyFieldValue(family),
    isSaving,
    create,
    remove,
  };
}
